import styled, { keyframes } from 'styled-components'
import { stoColor, stoMedia, stoRadius, stoSpace, stoType } from 'constants/stoTheme'

/** How long the ticker chip is held before the product list opens. */
export const HOLD_MS = 500
const SHEET_MOTION = '300ms cubic-bezier(0.4, 0, 0.2, 1)'
/**
 * The progress ring around the 32px chip icon, 2px clear of it, drawn like the logos' own ring:
 * dark grey, square-ended, from 12 o'clock most of the way round.
 */
const RING_WIDTH = 6
export const RING_RADIUS = 16 + 2 + RING_WIDTH / 2
/** Square box the ring is drawn in, centred on the icon. */
export const RING_BOX = 2 * (RING_RADIUS + RING_WIDTH / 2)
const RING_SWEEP = 0.75
/** The logos' own ring colour; not in the palette. */
const RING_COLOR = '#3f3f3f'
export const RING_LENGTH = 2 * Math.PI * RING_RADIUS

/** A quick side-to-side wiggle: "I can be pressed". */
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
`

/** Holds the chip and its hint, which hangs below it. Mobile only, like the chip. */
export const TriggerWrap = styled.span`
  position: relative;
  display: inline-flex;

  ${stoMedia.desktopUp} {
    display: none;
  }
`

/**
 * "Hold for more products" after a tap: below the chip, right-aligned to it at the screen's
 * edge, its (black) arrow under the icon. The library tooltip's look (it would flip to the side here).
 */
export const Hint = styled.span<{ $visible: boolean }>`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 10;
  width: max-content;
  padding: 12px;
  border-radius: ${stoRadius.lg};
  background: ${stoColor.surface};
  color: ${stoColor.black};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.4px;
  box-shadow:
    0 0 12px 8px rgba(0, 0, 0, 0.03),
    0 0 6px 3px rgba(0, 0, 0, 0.02);
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? 0 : '-4px')});
  transition:
    opacity 150ms ease,
    transform 150ms ease;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 10px;
    border: 6px solid transparent;
    border-bottom-color: ${stoColor.black};
  }
`

/** Mobile only: the ticker chip as a press-and-hold button. No selection or callout on a long press. */
export const Trigger = styled.button`
  display: inline-flex;
  padding: 0;
  border: none;
  border-radius: ${stoRadius.full};
  background: none;
  font: inherit;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  transition: transform 150ms ease;

  &[data-holding='true'] {
    transform: scale(0.96);
  }

  /*
   * A long press on an image starts iOS Safari's image drag: a lifted copy of the logo follows the
   * finger over the opened sheet, and the drag keeps the touches, so the sheet would not scroll.
   */
  img {
    -webkit-user-drag: none;
    user-select: none;
    pointer-events: none;
  }

  /* On the chip inside, so it does not fight the hold's scale on the button. */
  &[data-shake='true'] > * {
    animation: ${shake} 450ms ease-in-out;
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-shake='true'] > * {
      animation: none;
    }
  }

  &:focus-visible {
    outline: 2px solid ${stoColor.primary};
    outline-offset: 2px;
  }
`

export const IconRing = styled.span`
  position: relative;
  display: inline-flex;
  width: 32px;
  height: 32px;
`

/** Fills while the chip is held; drains quickly when it is let go early. */
export const Ring = styled.svg<{ $holding: boolean }>`
  position: absolute;
  inset: ${-(RING_BOX - 32) / 2}px;
  width: ${RING_BOX}px;
  height: ${RING_BOX}px;
  overflow: visible;
  transform: rotate(-90deg);
  pointer-events: none;

  circle {
    fill: none;
    stroke: ${RING_COLOR};
    stroke-width: ${RING_WIDTH};
    stroke-linecap: butt;
    stroke-dasharray: ${RING_LENGTH};
    stroke-dashoffset: ${({ $holding }) => ($holding ? RING_LENGTH * (1 - RING_SWEEP) : RING_LENGTH)};
    opacity: ${({ $holding }) => ($holding ? 1 : 0)};
    transition: ${({ $holding }) =>
      $holding ? `stroke-dashoffset ${HOLD_MS}ms linear` : 'stroke-dashoffset 200ms ease, opacity 200ms ease'};
  }

  @media (prefers-reduced-motion: reduce) {
    circle {
      transition: none;
    }
  }
`

export const Backdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  /* Above the site header and the key actions sheet. */
  z-index: 1003;
  background: rgba(0, 0, 0, 0.4);
  touch-action: none;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity ${SHEET_MOTION};
`

export const Sheet = styled.div<{ $open: boolean }>`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1004;
  max-height: 85dvh;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* As the key actions sheet. No bottom padding here: WebKit leaves it out of the scroll range (see List). */
  padding: 0 ${stoSpace[6]};
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-bottom: none;
  border-radius: ${stoRadius.lg} ${stoRadius.lg} 0 0;
  transform: translateY(${({ $open }) => ($open ? '0' : '100%')});
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition:
    transform ${SHEET_MOTION},
    visibility 0s ${({ $open }) => ($open ? '0s' : '300ms')};

  &:focus {
    outline: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/**
 * The title row (KeyActions' SheetHeader inside): stays at the top while the list scrolls under
 * it, edge to edge, with a soft shadow once there is something under it.
 */
export const SheetHead = styled.div<{ $scrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1;
  margin-inline: -${stoSpace[6]};
  padding: ${stoSpace[5]} ${stoSpace[6]};
  background: ${stoColor.surface};
  box-shadow: ${({ $scrolled }) =>
    $scrolled ? '0 6px 12px -6px rgba(0, 0, 0, 0.15)' : '0 6px 12px -6px rgba(0, 0, 0, 0)'};
  transition: box-shadow 150ms ease;
`

/** The dialog's heading, in the header row's own type. */
export const SheetTitle = styled.h2`
  margin: 0;
  font: inherit;
`

export const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[2]};
  margin: 0;
  /* The sheet's bottom padding: inside the content, so the last row scrolls clear on iOS too. */
  padding: 0 0 calc(${stoSpace[6]} + env(safe-area-inset-bottom, 0px));
  list-style: none;
`

export const Row = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${stoSpace[4]};
  padding: ${stoSpace[4]};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  background: ${({ $selected }) => ($selected ? stoColor.surfaceAlt : stoColor.surface)};
  color: ${({ $selected }) => ($selected ? stoColor.disabledText : stoColor.textPrimary)} !important;
  text-decoration: none;
`

/** A fixed slot, so the names line up even if a logo fails to load. */
export const RowIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
`

export const RowText = styled.span`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
`

/** The page's list-row type (Documents, Key information): callout title, regular meta. */
export const RowTitle = styled.span`
  ${stoType.bodyMdCallout};
`

export const RowMeta = styled.span<{ $selected?: boolean }>`
  ${stoType.bodyMd};
  color: ${({ $selected }) => ($selected ? stoColor.disabledText : stoColor.textSecondary)};
`

export const SelectedTag = styled.span`
  ${stoType.labelCta};
  flex: 0 0 auto;
  color: ${stoColor.disabledText};
`
